export function FasterClosingProcess() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block bg-teal-100 text-teal-800 px-6 py-2 rounded-full font-bold text-sm mb-4">
            BENEFIT #2
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Faster Closing Process
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Close your apartment building sale in days or weeks instead of months by eliminating bank bureaucracy and lengthy approval processes
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/b0ca0dfd9730216546328cbd58ecb783.jpg"
              alt="Fast seller financing closing process with minimal paperwork"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              Eliminate Bank Underwriting Delays
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              One of the most significant advantages of seller financing is the <strong>dramatically faster closing timeline</strong>. Traditional bank financing for apartment buildings can take 60-120 days or longer, involving extensive underwriting, appraisals, environmental assessments, property inspections, and multiple layers of approval.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              With seller financing, you bypass all of this bureaucracy. Since you're acting as the lender, there's no need to wait for bank approval, satisfy rigid lending criteria, or navigate complex institutional requirements. <strong>Transactions can close in as little as 7-30 days</strong>, depending on how quickly you can complete title work and basic legal documentation.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              This speed benefits both parties: sellers receive their down payment and start collecting monthly payments sooner, while buyers can secure the property before competing offers emerge. In competitive markets or time-sensitive situations, this speed advantage can be the difference between closing a deal and losing it.
            </p>

            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h4 className="text-xl font-bold text-gray-900 mb-4">What Gets Eliminated:</h4>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Bank Underwriting:</strong> No 30-60 day loan approval process
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Formal Appraisals:</strong> Optional or simplified property valuation
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Extensive Inspections:</strong> Can be waived or simplified by mutual agreement
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Loan Committee Approvals:</strong> No waiting for multiple approval layers
                  </span>
                </li>
                <li className="flex items-start">
                  <i className="ri-close-circle-line text-2xl text-red-500 mr-3 flex-shrink-0"></i>
                  <span className="text-gray-700">
                    <strong>Strict Debt-to-Income Requirements:</strong> Flexible qualification criteria
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Closing Timeline Comparison
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-bank-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Traditional Bank Financing</h4>
              </div>
              <div className="text-5xl font-bold text-red-600 mb-4">60-120 Days</div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-time-line text-xl text-red-500 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Loan application and documentation: 1-2 weeks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-xl text-red-500 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Property appraisal: 2-3 weeks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-xl text-red-500 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Environmental assessment: 2-4 weeks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-xl text-red-500 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Underwriting and approval: 3-6 weeks</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-time-line text-xl text-red-500 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Final approval and closing prep: 1-2 weeks</span>
                </li>
              </ul>
            </div>

            <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-teal-600 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-hand-coin-line text-2xl text-white"></i>
                </div>
                <h4 className="text-2xl font-bold text-gray-900">Seller Financing</h4>
              </div>
              <div className="text-5xl font-bold text-teal-600 mb-4">7-30 Days</div>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Negotiate terms and agreement: 3-7 days</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Title search and insurance: 5-10 days</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Legal documentation preparation: 3-7 days</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Optional inspections (if desired): 3-7 days</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-check-line text-xl text-teal-600 mr-2 flex-shrink-0 mt-1"></i>
                  <span>Closing and transfer: 1-2 days</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-2xl font-bold text-teal-600">
              Seller Financing Can Be <span className="text-4xl">4-8X Faster</span> Than Traditional Financing
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">Additional Speed Benefits</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3">Faster Access to Funds</h4>
              <p className="text-gray-300 leading-relaxed">
                Receive your down payment weeks or months earlier, giving you immediate access to capital
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3">Reduced Deal Risk</h4>
              <p className="text-gray-300 leading-relaxed">
                Less time for deals to fall through due to financing contingencies or market changes
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-check-line text-3xl text-white"></i>
              </div>
              <h4 className="text-xl font-bold mb-3">Flexible Scheduling</h4>
              <p className="text-gray-300 leading-relaxed">
                Close on your timeline without waiting for bank schedules and approval processes
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}